import React from 'react';
import { SearchIcons } from '../../../../../../../icons/icons';
import { Button, DataTable } from '../../../../../../../ui';
import { CustomSelect } from '../../../../../../../ui/custom_react_select/custom_select';
import { SearchIcon } from '../../../../../../../ui/icons/icons';
import inputStyles from '../../../../../../../ui/input/input.module.css';
import {
  CandidateJobApplicantsData,
  sortCandidates,
} from '../../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../../utils/types/react_select_types';
import { getColumns } from './Functions/GetColumns';

export interface CandidateListType {
  enabled: boolean;
  base64Encoded?: string;
  firstName: string;
  lastName: string;
  email: string;
  id: string;
  applicantStatus: string;
  invited: boolean;
  assessmentStatus: string;
  userAssessmentId?: string;
}

interface CandidateInvitesProps {
  handleSendInvites: () => void;
  handleRemoveCandidate: (targetId?: string) => void;
  candidatesList: CandidateListType[];
  loading: boolean;
  pageNumber: number;
  totalPages: number;
  totalRecords: number;
  selectedCandidates: string[];
  selectedCandidateIds: Set<string>;
  limit: number;
  handleSortChange: (key: string, direction: 'asc' | 'desc') => void;
  isRowSelected: (rowIndex: number, row: CandidateListType) => boolean;
  candidateInviteObject: CandidateJobApplicantsData;
  handleSelectRow: (rowIndex: number, row: CandidateListType) => void;
  sort: sortCandidates;
  handleSelectAll: () => void;
  handleStatusListChange: (
    label: string,
    selectedValue: SelectOptions[],
  ) => void;
  onPageChange: (page: number) => void;
  setLocalName: (localName: string) => void;
  setLocalEmail: (localEmail: string) => void;
  localName: string;
  localEmail: string;
  isAllSelected: boolean;
  isIndeterminate: boolean;
}

export const CandidateInvites: React.FC<CandidateInvitesProps> = ({
  handleSendInvites,
  handleRemoveCandidate,
  candidatesList,
  loading,
  pageNumber,
  totalPages,
  totalRecords,
  selectedCandidates,
  limit,
  handleSortChange,
  isRowSelected,
  candidateInviteObject,
  handleSelectRow,
  sort,
  handleSelectAll,
  handleStatusListChange,
  onPageChange,
  setLocalName,
  setLocalEmail,
  localName,
  localEmail,
  isAllSelected,
  isIndeterminate,
}) => {
  const applicantStatusType: SelectOptions[] = [
    { value: 'Outstanding', label: 'Outstanding' },
    { value: 'Suitable', label: 'Suitable' },
    { value: 'Auto-Assessment', label: 'Auto-Assessment' },
    { value: 'New', label: 'New' },
    { value: 'Shortlisted', label: 'Shortlisted' },
  ];
  const assessmentStatusType: SelectOptions[] = [
    { value: 'completed', label: 'Completed' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'expired', label: 'Expired' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'scoring', label: 'Scoring' },
  ];

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-1 md:grid-cols-6 gap-4 py-6 px-1 items-end bg-gray-50/50 rounded-xl'>
        <div className='flex flex-col gap-1.5'>
          <label className='text-xs font-bold text-gray-500 ml-1'>
            Name Search
          </label>
          <div className='relative'>
            <input
                className={`${inputStyles.inputStyles} !mt-0 !pl-9`}
                type='text'
                placeholder='Search by name...'
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
            />
            <div className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'>
              <SearchIcon className='w-4 h-4' />
            </div>
          </div>
        </div>
        <div className='flex flex-col gap-1.5'>
          <label className='text-xs font-bold text-gray-500 ml-1'>
            Email Search
          </label>
          <div className='relative'>
            <input
                className={`${inputStyles.inputStyles} !mt-0 !pl-9`}
                type='text'
                placeholder='Search by email...'
                value={localEmail}
                onChange={(e) => setLocalEmail(e.target.value)}
            />
            <div className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'>
              <SearchIcons className='w-4 h-4' />
            </div>
          </div>
        </div>
        <div className='flex flex-col gap-1.5'>
          <label className='text-xs font-bold text-gray-500 ml-1'>
            Applicant Status
          </label>
          <CustomSelect
              placeholder='All Statuses'
              name='applicantStatus'
              options={applicantStatusType}
              value={candidateInviteObject.applicantStatus.map((v: string) => ({
                label: v,
                value: v,
              }))}
              onChange={(opt: SelectOptions[]) =>
                handleStatusListChange('applicantStatus', opt)
              }
              isMulti
          />
        </div>
        <div className='flex flex-col gap-1.5'>
          <label className='text-xs font-bold text-gray-500 ml-1'>
            Assessment Status
          </label>
          <CustomSelect
              placeholder='All Assessment'
              name='assessmentStatus'
              options={assessmentStatusType}
              value={candidateInviteObject.assessmentStatus.map((v: string) => ({
                label: v,
                value: v,
              }))}
              onChange={(opt: SelectOptions[]) =>
                handleStatusListChange('assessmentStatus', opt)
              }
              isMulti
          />
        </div>

        <div className='flex flex-col gap-2'>
          <Button
              variant='primary'
              onClick={handleSendInvites}
              disabled={loading || selectedCandidates.length === 0}
              className='h-[42px] !rounded-lg'
              label={`Send Invites${
                selectedCandidates.length > 0
                  ? ` (${selectedCandidates.length})`
                  : ''
              }`}
          />
        </div>
        <div className='flex flex-col gap-2'>
          <Button
              variant='secondary'
              onClick={() => handleRemoveCandidate()}
              disabled={loading || selectedCandidates.length === 0}
              className='h-[42px] !rounded-lg'
              label='Remove Invite'
          />
        </div>
      </div>

      { candidatesList.length ? (
        <DataTable
            isLoading={loading}
            columns={getColumns((row) => handleRemoveCandidate(row.id))}
            data={candidatesList}
            rowsPerPage={limit}
            onPageChange={onPageChange}
            currentPage={pageNumber}
            totalPages={totalPages}
            onSortChange={handleSortChange}
            sortConfig={Object.entries(sort).map(([key, dir]) => ({
              key,
              direction: dir,
            }))}
            hasCheckBox
            onSelectAll={handleSelectAll}
            onSelectRow={handleSelectRow}
            isRowSelected={isRowSelected}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            totalRecords={totalRecords}
        />
      ) : (
        <div className='text-center py-10'>
          <div className='text-gray-800 text-lg'>No Applicants</div>
        </div>
      ) }
    </div>
  );
};
